import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import * as LucideIcons from "lucide-react";
import Navbar from "../../../sub-comp/Navbar";
import FullScreenImage from "../../../Home/ShowcaseSection";
import Footer from "../../../sub-comp/MainFooter";
import { getImageUrl } from "../../../../utils/getImageUrl";

const StandardProgramDetailTemplate = ({ programData }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger main animations after component mounts
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [programData]);

  // Parse JSON fields (may already be parsed or still a string)
  const learningItems = programData?.learningItems 
    ? (Array.isArray(programData.learningItems) 
        ? programData.learningItems 
        : (typeof programData.learningItems === 'string' ? JSON.parse(programData.learningItems) : []))
    : [];
  const careerOpportunities = programData?.careerOpportunitiesJson 
    ? (Array.isArray(programData.careerOpportunitiesJson) 
        ? programData.careerOpportunitiesJson 
        : (typeof programData.careerOpportunitiesJson === 'string' ? JSON.parse(programData.careerOpportunitiesJson) : []))
    : [];

  // Helper function to get icon component with color support
  const getIconComponent = (iconName, color = 'custom-pink') => {
    const IconComponent = LucideIcons[iconName] || LucideIcons.Code;
    // Map common color names to Tailwind classes
    const colorMap = {
      'blue': 'text-blue-600',
      'red': 'text-red-600',
      'green': 'text-green-600',
      'purple': 'text-purple-600',
      'orange': 'text-orange-600',
      'yellow': 'text-yellow-600',
      'pink': 'text-pink-600',
      'indigo': 'text-indigo-600',
      'custom-pink': 'text-custom-pink',
    };
    const colorClass = colorMap[color] || (color.startsWith('text-') ? color : `text-${color}-600`);
    return <IconComponent className={`w-8 h-8 ${colorClass} mb-4`} />;
  };

  // Default hero image if not provided - use utility to handle different path types
  const heroImage = getImageUrl(programData?.heroImage || '/assets/default-program.jpg');

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page min-h-screen bg-gray-50">
        {/* Header Section */}
        <div className="relative h-[42rem] bg-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt={programData?.heroTitle || 'Program'}
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              onError={(e) => {
                e.target.src = getImageUrl('/assets/default-program.jpg');
              }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 transition-opacity duration-500"></div>
          </div>

          <div className="relative z-10 flex items-center justify-center h-full">
            <div
              className={`text-center text-white transform transition-all duration-1000 ease-out ${
                isVisible
                  ? "translate-y-0 opacity-100"
                  : "translate-y-8 opacity-0"
              }`}
            >
              <h1 className="text-5xl md:text-6xl font-bold text-red-100 animate-pulse">
                {programData?.heroTitle || 'Program Title'}
              </h1>
              <p
                className={`text-xl mt-4 opacity-90 transform transition-all duration-1000 delay-300 ${
                  isVisible
                    ? "translate-y-0 opacity-90"
                    : "translate-y-4 opacity-0"
                }`}
              >
                {programData?.heroSubtitle || 'Program Subtitle'}
              </p>
            </div>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <Link
              to="/course/information-technology"
              className="flex items-center text-custom-pink hover:text-custom-pink/70 font-medium transition-all duration-300 hover:translate-x-1 group"
            >
              <ArrowLeft className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:-translate-x-1" />
              Back to IT Programs
            </Link>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="md:col-span-2">
              {/* Program Overview */}
              <section
                className={`mb-12 transform transition-all duration-800 ${
                  isVisible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-8 opacity-0"
                }`}
              >
                <h2 className="text-3xl font-bold text-gray-800 mb-6 relative">
                  {programData?.overviewTitle || 'Program Overview'}
                  <span className="absolute bottom-0 left-0 w-16 h-1 bg-custom-pink transform origin-left transition-transform duration-500 delay-500 scale-x-100"></span>
                </h2>
                <div className="prose prose-lg text-gray-600 space-y-4">
                  {programData?.overviewContent ? (
                    programData.overviewContent.split('\n\n').map((paragraph, index) => (
                      <p
                        key={index}
                        className={`transform transition-all duration-700 ${
                          isVisible
                            ? "translate-y-0 opacity-100"
                            : "translate-y-4 opacity-0"
                        }`}
                        style={{ transitionDelay: `${300 + index * 200}ms` }}
                      >
                        {paragraph}
                      </p>
                    ))
                  ) : (
                    <p>No overview content available.</p>
                  )}
                </div>
              </section>

              {/* What You'll Learn */}
              {learningItems.length > 0 && (
                <section
                  className={`mb-12 transform transition-all duration-800 delay-200 ${
                    isVisible
                      ? "translate-y-0 opacity-100"
                      : "translate-y-8 opacity-0"
                  }`}
                >
                  <h2 className="text-3xl font-bold text-gray-800 mb-6 relative">
                    {programData?.learningTitle || "What You'll Learn"}
                    <span className="absolute bottom-0 left-0 w-16 h-1 bg-custom-pink transform origin-left transition-transform duration-500 delay-700 scale-x-100"></span>
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    {learningItems.map((item, index) => (
                      <div
                        key={index}
                        className={`bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 group cursor-pointer transform ${
                          isVisible
                            ? "translate-y-0 opacity-100"
                            : "translate-y-8 opacity-0"
                        }`}
                        style={{
                          transitionDelay: `${800 + index * 150}ms`,
                        }}
                      >
                        <div className="transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                          {getIconComponent(item.icon, item.color)}
                        </div>
                        <h3 className="text-xl font-semibold mb-3 transition-colors duration-300 group-hover:text-custom-pink">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 transition-colors duration-300 group-hover:text-gray-700">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* Sidebar */}
            <div
              className={`md:col-span-1 transform transition-all duration-1000 delay-600 ${
                isVisible
                  ? "translate-y-0 opacity-100"
                  : "translate-y-8 opacity-0"
              }`}
            >
              {/* Program Details */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-6 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group">
                <h3 className="text-xl font-bold text-gray-800 mb-4 transition-colors duration-300 group-hover:text-custom-pink">
                  Program Details
                </h3>
                <div className="space-y-3">
                  {[
                    { label: "Duration:", value: programData?.detailsDuration || 'N/A' },
                    { label: "Format:", value: programData?.detailsFormat || 'N/A' },
                    { label: "Schedule:", value: programData?.detailsSchedule || 'N/A' },
                    { label: "Prerequisites:", value: programData?.detailsPrerequisites || 'N/A' },
                  ].map((detail, index) => (
                    <div
                      key={index}
                      className={`transform transition-all duration-500 hover:translate-x-2 ${
                        isVisible
                          ? "translate-y-0 opacity-100"
                          : "translate-y-2 opacity-0"
                      }`}
                      style={{
                        transitionDelay: `${1000 + index * 100}ms`,
                      }}
                    >
                      <span className="font-semibold text-gray-700">
                        {detail.label}
                      </span>
                      <span className="ml-2 text-gray-600">{detail.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Career Opportunities */}
              {careerOpportunities.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-6 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 transition-colors duration-300 group-hover:text-custom-pink">
                    {programData?.careerTitle || 'Career Opportunities'}
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    {careerOpportunities.map((career, index) => (
                      <li
                        key={index}
                        className={`hover:text-custom-pink transition-all duration-300 hover:translate-x-2 cursor-pointer transform ${
                          isVisible
                            ? "translate-y-0 opacity-100"
                            : "translate-y-2 opacity-0"
                        }`}
                        style={{
                          transitionDelay: `${1200 + index * 100}ms`,
                        }}
                      >
                        • {career}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Apply Now */}
              {(programData?.ctaTitle || programData?.ctaDescription) && (
                <div className="bg-custom-pink text-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group">
                  <h3 className="text-xl font-bold mb-4 group-hover:scale-105 transition-transform duration-300">
                    {programData?.ctaTitle || 'Ready to Start?'}
                  </h3>
                  <p className="mb-4 opacity-90 group-hover:opacity-100 transition-opacity duration-300">
                    {programData?.ctaDescription || 'Join our program and start your journey.'}
                  </p>
                  <Link to="/admissions" className="block w-full bg-yellow-500 text-black py-3 px-6 rounded-lg font-semibold hover:bg-yellow-600 transition-all duration-300 transform hover:scale-105 hover:shadow-lg active:scale-95 mb-3 text-center">
                    Apply Now
                  </Link>
                  <Link to="/contact" className="block w-full bg-transparent border-2 border-white text-white py-3 px-6 rounded-lg font-semibold hover:bg-white hover:text-custom-pink transition-all duration-300 transform hover:scale-105 hover:shadow-lg active:scale-95 text-center">
                    Request Info
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <FullScreenImage />
      <Footer />
    </div>
  );
};

export default StandardProgramDetailTemplate;


